import { useCallback, useEffect } from 'react';
import { usePatientNotesStore } from '@/stores/patientNotes/usePatientNotesStore';

interface UsePatientNotesParams {
  dietitianId: string;
  patientId: string;
  pageSize?: number;
}

export type UsePatientNotesReturn = {
  loading: boolean;
  error: string | null;
  submitError: string | null;
  submitting: boolean;
  draft: string;
  page: number;
  pageSize: number;
  totalItems: number;
  items: ReturnType<typeof usePatientNotesStore.getState>['items'];
  goToPage: (page: number) => Promise<void>;
  setDraft: (value: string) => void;
  addNote: (content: string) => Promise<void>;
  updateNote: (noteId: string, content: string) => Promise<void>;
  refresh: () => Promise<void>;
};

export function usePatientNotes({ dietitianId, patientId, pageSize = 10 }: UsePatientNotesParams): UsePatientNotesReturn {
  const loading = usePatientNotesStore((s) => s.loading);
  const error = usePatientNotesStore((s) => s.error);
  const submitError = usePatientNotesStore((s) => s.submitError);
  const submitting = usePatientNotesStore((s) => s.submitting);
  const page = usePatientNotesStore((s) => s.page);
  const pageSizeState = usePatientNotesStore((s) => s.pageSize);
  const totalItems = usePatientNotesStore((s) => s.totalItems);
  const items = usePatientNotesStore((s) => s.items);
  const draft = usePatientNotesStore((s) => s.draft);
  const load = usePatientNotesStore((s) => s.load);
  const refreshStore = usePatientNotesStore((s) => s.refresh);
  const reset = usePatientNotesStore((s) => s.reset);
  const setDraft = usePatientNotesStore((s) => s.setDraft);
  const addNoteToStore = usePatientNotesStore((s) => s.addNote);
  const updateNoteInStore = usePatientNotesStore((s) => s.updateNote);

  useEffect(() => {
    reset();
    void load(dietitianId, patientId, 1, pageSize);
  }, [reset, load, dietitianId, patientId, pageSize]);

  const goToPage = useCallback(
    async (targetPage: number) => {
      await load(dietitianId, patientId, targetPage, pageSize);
    },
    [load, dietitianId, patientId, pageSize]
  );

  const refresh = useCallback(async () => {
    await refreshStore();
  }, [refreshStore]);

  return {
    loading,
    error,
    submitError,
    submitting,
    draft,
    page,
    pageSize: pageSizeState,
    totalItems,
    items,
    goToPage,
    setDraft,
    addNote: async (content: string) => {
      await addNoteToStore(dietitianId, patientId, content);
    },
    updateNote: async (noteId: string, content: string) => {
      await updateNoteInStore(dietitianId, patientId, noteId, content);
    },
    refresh,
  };
}

export default usePatientNotes;
